import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Loader2, ArrowLeft, ExternalLink } from 'lucide-react';
import { TekWissenCertificate } from '../components/certificate/TekWissenCertificate';
import { ScaledCertificate } from '../components/certificate/ScaledCertificate';
import { rpatAdvancedCertificate } from '../components/certificate/certificateContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Page = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4 sm:p-8">{children}</div>
);

// Public page: anyone with the link can confirm a TekWissen Certified Recruiter certificate
const CertificateVerify = () => {
  const { certificateNumber } = useParams();
  const [state, setState] = useState({ loading: true, certificate: null, error: null });

  useEffect(() => {
    axios.get(`${API_URL}/api/rpat/certification/verify/${certificateNumber}`)
      .then((res) => setState({ loading: false, certificate: res.data, error: null }))
      .catch((err) => setState({ loading: false, certificate: null, error: err.response?.data?.detail || 'Certificate not found or invalid' }));
  }, [certificateNumber]);

  if (state.loading) {
    return <Page><div className="text-center"><Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-cyan-600" /><p className="text-lg text-slate-700">Verifying certificate...</p></div></Page>;
  }

  if (state.error) {
    return (
      <Page>
        <Card className="w-full max-w-md" data-testid="certificate-verify-invalid">
          <CardContent className="pb-6 pt-8 text-center">
            <div className="mb-4 inline-flex rounded-full bg-red-100 p-4"><XCircle className="h-12 w-12 text-red-500" /></div>
            <h1 className="mb-2 text-2xl font-bold text-gray-800">Invalid Certificate</h1>
            <p className="mb-6 text-gray-600">{state.error}</p>
            <p className="mb-4 text-sm text-gray-500">Certificate Number: <code className="rounded bg-gray-100 px-2 py-1">{certificateNumber}</code></p>
            <Link to="/"><Button variant="outline"><ArrowLeft className="mr-2 h-4 w-4" />Go to Homepage</Button></Link>
          </CardContent>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <div className="w-full max-w-5xl space-y-6" data-testid="certificate-verify-page">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-50 px-4 py-2" data-testid="certificate-verify-valid">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <span className="font-medium text-emerald-700">Verified Certificate</span>
          </div>
        </div>
        <ScaledCertificate><TekWissenCertificate data={rpatAdvancedCertificate(state.certificate)} /></ScaledCertificate>
        <div className="text-center">
          <p className="mb-3 text-sm text-slate-500">This certificate is issued by TekWissen Group and can be independently verified.</p>
          <a href="https://tekwissen.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-cyan-700 hover:text-cyan-800">
            Learn more about TekWissen<ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </Page>
  );
};

export default CertificateVerify;
